import { Container, ThemeProvider, theme } from '@everlywell/ui-kit';
import WelcomeToMembership from 'components/Membership/WelcomeToMembership';
import { parseWelcomeToMembership } from 'components/Membership/WelcomeToMembership/WelcomeToMembership.parser';
import { useMembershipCheckout } from 'components/MembershipUtils/MembershipCheckoutContext/MembershipCheckoutContext';
import { SEO } from 'components/SEO';
import parseSeoData from 'components/SEO/Seo.parser';
import React, { useEffect } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { isBrowser } from 'utils/helpers';
import { gtm } from 'utils/trackers';

export type MembershipConfirmationProps = {
  pageContext: {
    metadata: Queries.ContentfulMetadata001;
    seo: Queries.ContentfulSeo001;
    sections: Queries.ContentfulSection001[];
    slug: string;
  };
};

type ComponentMapping = Record<
  string,
  {
    component: React.FC<any>;
    parser: (section: Queries.ContentfulSection001) => any;
  }
>;

const components: ComponentMapping = {
  WelcomeToMembership: {
    component: WelcomeToMembership,
    parser: parseWelcomeToMembership,
  },
};

/**
 * Membership Confirmation Page
 * @param {MembershipConfirmationProps} props
 * @returns {JSX.Element} Membership Confirmation Page
 */
export default function MembershipConfirmation({
  pageContext,
}: MembershipConfirmationProps): JSX.Element {
  const { seo, sections } = pageContext;
  const { program, setRedirectedToConfirmation } = useMembershipCheckout();

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        page: ANALYTICS.MEMBERSHIP.WELCOME_PAGE.TITLE,
        program: program || 'No program provided',
      },
    });

    isBrowser() &&
      gtm.track({
        event: 'welcomeToProgramPage',
        programName: program,
      });
  }, [program]);

  useEffect(() => {
    setRedirectedToConfirmation(true);
    // setRedirectedToConfirmation is a function from useState and it's guaranteed not to change during the lifetime of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {seo ? <SEO {...parseSeoData(seo)} /> : <SEO title="Confirmation" />}

      <Container size={['fluid', null, null, null, 'ultra']} margin="0 auto">
        {sections.map((section) => {
          if (!section.componentName) {
            return null;
          }

          const sectionMapItem = components[section.componentName];

          if (!sectionMapItem) {
            return null;
          }

          const componentProps = {
            ...sectionMapItem.parser(section),
            program,
          };

          return React.createElement(sectionMapItem.component, {
            ...componentProps,
            key: section.id,
          });
        })}
      </Container>
    </ThemeProvider>
  );
}
